import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  LinearProgress,
  Snackbar,
  useTheme,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Speed as SpeedIcon,
  BugReport as BugReportIcon
} from '@mui/icons-material';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement
} from 'chart.js';
import dataQualityService from '../../services/dataQualityService';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement
);

const RuleImpact = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [selectedRule, setSelectedRule] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [ruleImpactData, setRuleImpactData] = useState({
    totalRules: 0,
    totalErrors: 0,
    criticalIssues: 0,
    averageImpact: 0,
    rules: [],
    distribution: {}
  });

  const fetchImpactData = useCallback(async () => {
    setLoading(true);
    try {
      const [impact, distribution, rules] = await Promise.all([
        dataQualityService.getImpactAnalysis(),
        dataQualityService.getErrorDistribution(),
        dataQualityService.getRules()
      ]);

      const impactRules = (impact?.rules ?? impact ?? []).map(r => ({
        id: r.ruleId || r.rule_id || r.id,
        name: r.ruleName || r.rule_name || r.name,
        category: r.category || r.dimension_type || 'General',
        severity: r.severity || (r.errorCount > 100 ? 'high' : r.errorCount > 50 ? 'medium' : 'low'),
        errorCount: parseInt(r.errorCount || r.error_count || r.failure_count || 0),
        impactPercentage: parseFloat(r.impactPercent || r.impact_percentage || r.impact || 0),
        affectedEntities: parseInt(r.affectedEntities || r.affected_entities || r.entity_count || 0),
        executionTime: r.execution_time || '-',
        trend: r.trend || 'stable',
        trendPercentage: parseFloat(r.trend_percentage || 0),
        description: r.description || '',
        failureReasons: r.failure_reasons || []
      }));

      const totalErrors = impactRules.reduce((sum, r) => sum + r.errorCount, 0);
      const ruleList = rules?.rules ?? rules ?? [];

      setRuleImpactData({
        totalRules: ruleList.length,
        totalErrors,
        criticalIssues: impactRules.filter(r => r.severity === 'high' || r.severity === 'critical').length,
        averageImpact: impactRules.length > 0
          ? Math.round(impactRules.reduce((sum, r) => sum + r.impactPercentage, 0) / impactRules.length * 10) / 10
          : 0,
        rules: impactRules,
        distribution: distribution || {}
      });
    } catch (err) {
      console.error('Error fetching impact data:', err);
      setSnackbar({ open: true, message: `Failed to load impact data: ${err.message}`, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchImpactData();
  }, [fetchImpactData]);

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return theme.palette.error.main;
      case 'high': return theme.palette.warning.main;
      case 'medium': return theme.palette.info.main;
      case 'low': return theme.palette.success.main;
      default: return theme.palette.grey[500];
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return <ErrorIcon />;
      case 'high': return <WarningIcon />;
      case 'medium': return <InfoIcon />;
      case 'low': return <CheckCircleIcon />;
      default: return <WarningIcon />;
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'increasing': return <TrendingUpIcon color="error" />;
      case 'decreasing': return <TrendingDownIcon color="success" />;
      case 'stable': return <SpeedIcon color="info" />;
      default: return <SpeedIcon />;
    }
  };

  const filteredRules = ruleImpactData.rules.filter(rule => {
    const matchesSearch = rule.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         rule.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || rule.category === categoryFilter;
    const matchesSeverity = severityFilter === 'all' || rule.severity === severityFilter;
    return matchesSearch && matchesCategory && matchesSeverity;
  });

  const impactChartData = {
    labels: filteredRules.slice(0, 10).map(r => r.name.length > 20 ? r.name.substring(0, 20) + '...' : r.name),
    datasets: [{
      label: 'Error Count',
      data: filteredRules.slice(0, 10).map(r => r.errorCount),
      backgroundColor: filteredRules.slice(0, 10).map(r => getSeverityColor(r.severity) + '80'),
      borderColor: filteredRules.slice(0, 10).map(r => getSeverityColor(r.severity)),
      borderWidth: 1
    }]
  };

  const categories = [...new Set(ruleImpactData.rules.map(r => r.category))];
  const categoryDistribution = {
    labels: categories,
    datasets: [{
      data: categories.map(cat => ruleImpactData.rules.filter(r => r.category === cat).reduce((sum, r) => sum + r.errorCount, 0)),
      backgroundColor: [
        theme.palette.error.light,
        theme.palette.warning.light,
        theme.palette.info.light,
        theme.palette.success.light,
        theme.palette.secondary.light
      ],
      borderColor: [
        theme.palette.error.main,
        theme.palette.warning.main,
        theme.palette.info.main,
        theme.palette.success.main,
        theme.palette.secondary.main
      ],
      borderWidth: 2
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'top' }, title: { display: false } },
    scales: { y: { beginAtZero: true } }
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'bottom' } }
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
        <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>Loading rule impact analysis...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5">Rule Impact Analysis</Typography>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchImpactData} disabled={loading}>Refresh</Button>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card><CardContent>
            <Typography color="textSecondary" gutterBottom variant="body2">TOTAL RULES</Typography>
            <Typography variant="h4">{ruleImpactData.totalRules}</Typography>
            <Typography variant="body2" color="text.secondary">In quality rule engine</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card><CardContent>
            <Typography color="textSecondary" gutterBottom variant="body2">TOTAL ERRORS</Typography>
            <Typography variant="h4" color="error.main">{ruleImpactData.totalErrors.toLocaleString()}</Typography>
            <Typography variant="body2" color="text.secondary">All rule violations</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card><CardContent>
            <Typography color="textSecondary" gutterBottom variant="body2">HIGH SEVERITY</Typography>
            <Typography variant="h4" color="warning.main">{ruleImpactData.criticalIssues}</Typography>
            <Typography variant="body2" color="text.secondary">High impact rules</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card><CardContent>
            <Typography color="textSecondary" gutterBottom variant="body2">AVERAGE IMPACT</Typography>
            <Typography variant="h4">{ruleImpactData.averageImpact}%</Typography>
            <Typography variant="body2" color="text.secondary">Per rule impact</Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search rules..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{ minWidth: 200 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Category</InputLabel>
          <Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} label="Category">
            <MenuItem value="all">All Categories</MenuItem>
            {categories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Severity</InputLabel>
          <Select value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)} label="Severity">
            <MenuItem value="all">All Severities</MenuItem>
            <MenuItem value="critical">Critical</MenuItem>
            <MenuItem value="high">High</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="low">Low</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Rule Error Distribution</Typography>
            <Box sx={{ height: 300, mt: 2 }}>
              {filteredRules.length > 0 ? (
                <Bar data={impactChartData} options={chartOptions} />
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', pt: 10 }}>No impact data</Typography>
              )}
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Errors by Category</Typography>
            <Box sx={{ height: 300, mt: 2 }}>
              {categories.length > 0 ? (
                <Doughnut data={categoryDistribution} options={doughnutOptions} />
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', pt: 10 }}>No category data</Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Rule Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell align="right">Errors</TableCell>
                <TableCell align="right">Impact %</TableCell>
                <TableCell align="right">Affected Entities</TableCell>
                <TableCell>Trend</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRules
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((rule) => (
                  <TableRow key={rule.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">{rule.name}</Typography>
                    </TableCell>
                    <TableCell><Chip label={rule.category} size="small" color="primary" variant="outlined" /></TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {getSeverityIcon(rule.severity)}
                        <Typography variant="body2" sx={{ ml: 1 }} color={getSeverityColor(rule.severity)}>{rule.severity}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="error.main">{rule.errorCount.toLocaleString()}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(rule.impactPercentage, 100)}
                          sx={{
                            width: 60, mr: 1, height: 6, borderRadius: 3,
                            backgroundColor: theme.palette.grey[200],
                            '& .MuiLinearProgress-bar': { backgroundColor: getSeverityColor(rule.severity), borderRadius: 3 }
                          }}
                        />
                        <Typography variant="body2" sx={{ minWidth: 40 }}>{rule.impactPercentage}%</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">{rule.affectedEntities.toLocaleString()}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {getTrendIcon(rule.trend)}
                        <Typography variant="body2" sx={{ ml: 1 }}>
                          {rule.trendPercentage > 0 ? '+' : ''}{rule.trendPercentage}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => { setSelectedRule(rule); setDialogOpen(true); }}>
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredRules.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </Paper>

      <Dialog open={dialogOpen} onClose={() => { setDialogOpen(false); setSelectedRule(null); }} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <BugReportIcon sx={{ mr: 1 }} />
            Rule Impact Details: {selectedRule?.name}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRule && (
            <Box sx={{ pt: 2 }}>
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>Rule Overview</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">Description</Typography>
                    <Typography variant="body1">{selectedRule.description || 'No description available'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="text.secondary">Category</Typography>
                    <Typography variant="body1">{selectedRule.category}</Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="text.secondary">Severity</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                      {getSeverityIcon(selectedRule.severity)}
                      <Typography variant="body1" sx={{ ml: 1, textTransform: 'capitalize' }}>{selectedRule.severity}</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>Impact Metrics</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">Total Errors</Typography>
                    <Typography variant="h5" color="error.main">{selectedRule.errorCount.toLocaleString()}</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">Impact</Typography>
                    <Typography variant="h5">{selectedRule.impactPercentage}%</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">Affected Entities</Typography>
                    <Typography variant="h5">{selectedRule.affectedEntities.toLocaleString()}</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">Trend</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      {getTrendIcon(selectedRule.trend)}
                      <Typography variant="h6" sx={{ ml: 1 }}>
                        {selectedRule.trendPercentage > 0 ? '+' : ''}{selectedRule.trendPercentage}%
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {selectedRule.failureReasons && selectedRule.failureReasons.length > 0 && (
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>Failure Breakdown</Typography>
                  <List>
                    {selectedRule.failureReasons.map((reason, index) => (
                      <React.Fragment key={index}>
                        <ListItem>
                          <ListItemIcon><ErrorIcon color="error" /></ListItemIcon>
                          <ListItemText
                            primary={reason.reason || reason.message}
                            secondary={
                              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
                                  {(reason.count || 0).toLocaleString()} errors
                                </Typography>
                                <LinearProgress
                                  variant="determinate"
                                  value={reason.percentage || 0}
                                  sx={{
                                    width: 100, mr: 1, height: 6, borderRadius: 3,
                                    backgroundColor: theme.palette.grey[200],
                                    '& .MuiLinearProgress-bar': { backgroundColor: theme.palette.error.main, borderRadius: 3 }
                                  }}
                                />
                                <Typography variant="body2" color="text.secondary">{reason.percentage || 0}%</Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < selectedRule.failureReasons.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </Paper>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDialogOpen(false); setSelectedRule(null); }}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default RuleImpact;
